import {Injectable, Injector} from '@angular/core';
import {BehaviorSubject, Subscription} from "rxjs";
import {Apollo} from "apollo-angular";
import {
  get_package_files,
  get_equity_installments,
  get_funding_by_id,
  get_installment_by_id,
  get_installment_funding_payments,
  get_log_history,
} from "./graphql/getEquityInstallments";
import {
  create_condition_approval,
  create_equity_installment,
  create_funding_condition_file,
  create_installment_funding,
  create_project_installment_funding_condition,
  delete_condition_approval,
  delete_condition_file,
  delete_equity_installment,
  delete_installment_funding,
  delete_installment_funding_payment,
  delete_project_installment_funding_condition,
  generate_installment_funding_payments,
  generate_package,
  delete_package,
  update_condition_approval,
  update_equity_installment,
  update_include_in_package_status,
  update_installment_funding,
  update_installment_funding_payment,
  update_project_installment_funding_condition
} from "./graphql/updateEquityInstallments";
import ProjectEquityInstallment from "../interfaces/project-equity-installment";
import ProjectEquityInstallmentFunding from "../interfaces/project-installment-funding";
import ProjectInstallmentStatus from "../interfaces/project-installment-status";
import ProjectInstallmentFundingStatus from "../interfaces/project-installment-funding-status";
import ProjectInstallmentFundingConditionStatus from "../interfaces/project-installment-funding-condition-status";
import ProjectInstallmentFundingConditionApprovalStatus
  from "../interfaces/project-installment-funding-condition-approval-status";
import ProjectInstallmentFundingConditionApprovalType
  from "../interfaces/project-installment-funding-condition-approval-type";
import {MatSnackBar, MatSnackBarConfig} from "@angular/material/snack-bar";
import ProjectInstallmentFundingConditions from "../interfaces/project-installment-funding-conditions";
import ProjectInstallmentFundingConditionApproval from "../interfaces/project-installment-funding-condition-approval";
import ProjectInstallmentFundingConditionFile from "../interfaces/project-installment-funding-condition-file";
import {checkPermission} from '../pipes/permission.pipe';
import Permission from "../interfaces/permission";
import ID from "../types/id";
import {
  InstallmentViewType
} from "./equity-installment-subcomponents/equity-installment-home/equity-installment-home.component";
import ProjectInstallmentFundingPayment from "../interfaces/project-installment-funding-payment";
import {
  EquityInstallmentSharepointService
} from "./equity-installment-subcomponents/equity-installment-documents-panel/equity-installment-sharepoint.service";

export enum UpdateInstallmentObservableMode {
  UPDATE = 'update',
  ADD = 'add',
  REMOVE = 'remove'
}

type InstallmentPermissions = {
  installmentPermissions?: Permission | null
  fundingPermissions?: Permission | null
  conditionsPermissions?: Permission | null
  approvalPermissions?: Permission | null
  paymentPermissions?: Permission | null
  packagePermissions?: Permission | null
  filePermissions?: Permission | null
  fundingStatusPermissions?: Permission | null
}
@Injectable({
  providedIn: 'root'
})



export class EquityInstallmentService {
  private _installmentFundings:any = {}
  private installmentFundings = new BehaviorSubject(this._installmentFundings)
  public installmentFundings$ = this.installmentFundings.asObservable()

  public serviceLoaded:boolean = false

  private _projectInstallmentFundingStatuses:ProjectInstallmentFundingStatus[] = []
  private projectInstallmentFundingStatuses = new BehaviorSubject(this._projectInstallmentFundingStatuses)
  public projectInstallmentFundingStatuses$ = this.projectInstallmentFundingStatuses.asObservable()

  private _projectInstallments: ProjectEquityInstallment[] = []
  private projectInstallments = new BehaviorSubject(this._projectInstallments)
  public projectInstallments$ = this.projectInstallments.asObservable()

  private _projectFundingConditions: any = {}
  private projectFundingConditions = new BehaviorSubject(this._projectFundingConditions)
  public projectFundingConditions$ = this.projectFundingConditions.asObservable()

  private _projectConditionFiles: any = {}
  private projectConditionFiles = new BehaviorSubject(this._projectConditionFiles)
  public projectConditionFiles$ = this.projectConditionFiles.asObservable();

  private _projectConditionApprovals: any = {}
  private projectConditionApprovals = new BehaviorSubject(this._projectConditionApprovals)
  public projectConditionApprovals$ = this.projectConditionApprovals.asObservable();

  private _projectFundingPayments:any = {}
  private projectFundingPayments = new BehaviorSubject(this._projectFundingPayments)
  public projectFundingPayments$ = this.projectFundingPayments.asObservable();

  private _projectInstallmentStatuses: ProjectInstallmentStatus[] = []
  private projectInstallmentStatuses = new BehaviorSubject(this._projectInstallmentStatuses)
  public projectInstallmentStatuses$ = this.projectInstallmentStatuses.asObservable()

  private _projectInstallmentConditionStatuses: ProjectInstallmentFundingConditionStatus[] = []
  private projectInstallmentConditionStatuses = new BehaviorSubject(this._projectInstallmentConditionStatuses)
  public projectInstallmentConditionStatuses$ = this.projectInstallmentConditionStatuses.asObservable()

  private _projectConditionApprovalStatuses: ProjectInstallmentFundingConditionApprovalStatus[] = []
  private projectConditionApprovalStatuses = new BehaviorSubject(this._projectConditionApprovalStatuses)
  public projectConditionApprovalStatuses$ = this.projectConditionApprovalStatuses.asObservable()

  private _projectConditionApprovalTypes: ProjectInstallmentFundingConditionApprovalType[] = []
  private projectConditionApprovalTypes = new BehaviorSubject(this._projectConditionApprovalTypes)
  public projectConditionApprovalTypes$ = this.projectConditionApprovalTypes.asObservable();

  private _erroredFiles: any  = {};
  private erroredFiles = new BehaviorSubject(this._erroredFiles)
  public erroredFiles$ = this.erroredFiles.asObservable();

  private _project:any = {};
  private project = new BehaviorSubject(this._project)
  public project$ = this.project.asObservable()

  private _user:any = {};
  private user = new BehaviorSubject(this._user)
  public user$ = this.user.asObservable();

  public viewType: InstallmentViewType = InstallmentViewType.SYNDICATED

  private _permissions:InstallmentPermissions | null = null;
  private permissions = new BehaviorSubject(this._permissions)
  public permissions$ = this.permissions.asObservable();

  private _loading = true;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  constructor(private apollo: Apollo, private _snackBar: MatSnackBar, private injector: Injector) {
  }

  private _setProject(project:any){
    this._project = project;
    this.project.next(this._project)
  }

  private _setInstallments(installments:ProjectEquityInstallment[]){
    this._projectInstallments = installments
    this.projectInstallments.next(this._projectInstallments)
  }

  private _setInstallmentStatuses(statuses: ProjectInstallmentStatus[]){
    this._projectInstallmentStatuses = statuses
    this.projectInstallmentStatuses.next(this._projectInstallmentStatuses)
  }

  private _setInstallmentFundingStatuses(statuses: ProjectInstallmentFundingStatus[]){
    this._projectInstallmentFundingStatuses = statuses
    this.projectInstallmentFundingStatuses.next(this._projectInstallmentFundingStatuses)
  }

  private _setInstallmentConditionStatuses(statuses: ProjectInstallmentFundingConditionStatus[]){
    this._projectInstallmentConditionStatuses = statuses
    this.projectInstallmentConditionStatuses.next(this._projectInstallmentConditionStatuses)
  }

  private _setApprovalStatuses(statuses: ProjectInstallmentFundingConditionApprovalStatus[]){
    this._projectConditionApprovalStatuses = statuses
    this.projectConditionApprovalStatuses.next(this._projectConditionApprovalStatuses)
  }

  private _setApprovalTypes(types: ProjectInstallmentFundingConditionApprovalType[]){
    this._projectConditionApprovalTypes = types
    this.projectConditionApprovalTypes.next(this._projectConditionApprovalTypes)
  }

  private _setFundings(installments:ProjectEquityInstallment[]){
    for(let j = 0; j < installments.length; j++){
      this._installmentFundings[installments[j].id] = []
      this._installmentFundings[installments[j].id] = installments[j].projectInstallmentFundings
      this._setConditions(installments[j].projectInstallmentFundings)
      this._setFundingPayments(installments[j].projectInstallmentFundings)
      this.installmentFundings.next(this._installmentFundings)
    }
  }

  private _setConditions(fundings: ProjectEquityInstallmentFunding[]){
    for(let f = 0; f < fundings.length; f++){
      this._projectFundingConditions[fundings[f].id] = []
      this._projectFundingConditions[fundings[f].id] = fundings[f].projectInstallmentFundingConditions
      this._setConditionFiles(fundings[f].projectInstallmentFundingConditions)
      this._setConditionApprovals(fundings[f].projectInstallmentFundingConditions)
      this.projectFundingConditions.next(this._projectFundingConditions)
    }
  }

  private _setConditionFiles(conditions: ProjectInstallmentFundingConditions[]){
    for(let c = 0; c < conditions.length; c++){
      this._projectConditionFiles[conditions[c].id] = []
      this._projectConditionFiles[conditions[c].id] = conditions[c].projectInstallmentFundingConditionFiles
      //this.setErroredFiles(conditions[c].projectInstallmentFundingConditionFiles, conditions[c].projectInstallmentFunding.id, conditions[c].id)
      this.projectConditionFiles.next(this._projectConditionFiles)
    }
  }

  private _setConditionApprovals(conditions: ProjectInstallmentFundingConditions[]){
    for(let c = 0; c < conditions.length; c++){
      this._projectConditionApprovals[conditions[c].id] = []
      this._projectConditionApprovals[conditions[c].id] = conditions[c].projectInstallmentFundingConditionApprovals
      this.projectConditionApprovals.next(this._projectConditionApprovals)
    }
  }

  private _setFundingPayments(fundings: ProjectEquityInstallmentFunding[]){
    for(let f = 0; f < fundings.length; f++){
      this._projectFundingPayments[fundings[f].id] = []
      this._projectFundingPayments[fundings[f].id] = fundings[f].projectInstallmentFundingPayments
      this.projectFundingPayments.next(this._projectFundingPayments)
    }
  }

  private _setPermissions(user: any){
    let _permission = {
      installmentPermissions: null,
      fundingPermissions: null,
      conditionPermissions: null,
      approvalPermissions: null,
      filePermissions: null,
      paymentPermissions:null,
      fundingStatusPermissions: null,
      packagePermissions: null
    }
    if(user.installmentPermissions!==null){
      _permission.installmentPermissions = user.installmentPermissions
    }
    if(user.fundingPermissions!==null){
      _permission.fundingPermissions = user.fundingPermissions
    }
    if(user.conditionPermissions!==null){
      _permission.conditionPermissions = user.conditionPermissions
    }
    if(user.approvalPermissions!==null){
      _permission.approvalPermissions = user.approvalPermissions
    }
    if(user.filePermissions!==null){
      _permission.filePermissions = user.filePermissions
    }
    if(user.paymentPermissions!==null){
      _permission.paymentPermissions = user.paymentPermissions
    }
    if(user.fundingStatusPermissions!==null){
      _permission.fundingStatusPermissions = user.fundingStatusPermissions
    }
    if(user.packagePermissions!==null){
      _permission.packagePermissions = user.packagePermissions
    }
    this._permissions = _permission
    this.permissions.next(this._permissions)
  }

  private _setUser(user:any){
    this._user = user;
    this.user.next(this._user)
  }

  private setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.next(this._loading);
  }

  public expandedFundings = new Set<string>();

  public setErroredFiles(files:ProjectInstallmentFundingConditionFile[], funding_id:string, condition_id: string){
    const sharepoint = this.injector.get(EquityInstallmentSharepointService);
    //<----- EQ Installments enhancement ------------------->



      (async () => {
          const checks = files.map(file =>
            sharepoint.checkValidFiles(file.fileurl ?? '', this._project.sharepointfolder).then((result:any) => ({
              file,
              result
            })))

          const results = await Promise.all(checks)

          results.forEach((file:any, result:any) => {
            if (result.status === 'error') {
              if (this._erroredFiles.hasOwnProperty(funding_id)) {
                if (this._erroredFiles[funding_id].hasOwnProperty(condition_id)) {
                  this._erroredFiles[funding_id][condition_id].push(file.filename)
                } else {
                  this._erroredFiles[funding_id][condition_id] = []
                  this._erroredFiles[funding_id][condition_id].push(file.filename)
                }
              } else {
                this._erroredFiles[funding_id] = {}
                this._erroredFiles[funding_id][condition_id] = []
                this._erroredFiles[funding_id][condition_id].push(file.filename)
              }
            }
          })
          this.erroredFiles.next(this._erroredFiles)
      })();
    // for(let file of files){
    //   sharepoint.checkValidFiles(file.fileurl ?? '', this._project.sharepoint_folder).then((result) => {
    //     if(result.hasOwnProperty('status') && result.status == 'error'){
    //       if(this._erroredFiles.hasOwnProperty(funding_id)){
    //         if(this._erroredFiles[funding_id].hasOwnProperty(condition_id)){
    //           this._erroredFiles[funding_id][condition_id].push(file.filename)
    //           this.erroredFiles.next(this._erroredFiles)
    //         } else {
    //           this._erroredFiles[funding_id][condition_id] = []
    //           this._erroredFiles[funding_id][condition_id].push(file.filename)
    //           this.erroredFiles.next(this._erroredFiles)
    //         }
    //       } else {
    //         this._erroredFiles[funding_id] = {}
    //         this._erroredFiles[funding_id][condition_id] = []
    //         this._erroredFiles[funding_id][condition_id].push(file.filename)
    //         this.erroredFiles.next(this._erroredFiles)
    //       }
    //     }
    //   })
    // }

  }


  public fetchProjectInstallments(id:string, initialize?: boolean){
    if(initialize) this.setLoading(true)
    let subscription = this.apollo.query({
      query: get_equity_installments,
      variables: {
        project_id: id
      },
      fetchPolicy: 'no-cache'
    }).subscribe(({data}:any) => {
      let result:any = data
      this._setProject(result.project)
      this._setPermissions(result.me)
      this._setUser(result.me)
      this._setInstallments(result.project_installments_by_project)
      this._setFundings(result.project_installments_by_project)
      this._setInstallmentStatuses(result.project_installment_statuses)
      this._setInstallmentFundingStatuses(result.project_installment_funding_statuses)
      this._setInstallmentConditionStatuses(result.project_installment_funding_condition_statuses)
      this._setApprovalStatuses(result.project_installment_funding_condition_approval_statuses)
      this._setApprovalTypes(result.project_installment_funding_condition_approval_types)
      if (initialize) this.setLoading(false)
      this.serviceLoaded = true
      subscription.unsubscribe()
    })
  }

  public refresh(){
    let subscription = this.apollo.watchQuery({
      query: get_equity_installments,
      variables: {
        project_id: this._project.id
      }
    }).valueChanges.subscribe((result:any) => {
      let data = result.data;
      this._setInstallments(data.project_installments_by_project)
      this._setFundings(data.project_installments_by_project)
      subscription.unsubscribe()
    })
  }

  public refreshPackageList(id:string){
    return this.apollo.query({
      query: get_package_files,
      variables: {
        project_installment_funding_id: id
      },
      fetchPolicy: 'network-only'
    })
  }

  public updateInstallments(mode: UpdateInstallmentObservableMode, installment: ProjectEquityInstallment){
    if(mode===UpdateInstallmentObservableMode.ADD){
      if(this._projectInstallments.length==0){
        this._projectInstallments = []
      }
      this._projectInstallments.push(installment)
      this.projectInstallments.next(this._projectInstallments)
      this._setFundings(this._projectInstallments)
    }
    if(mode===UpdateInstallmentObservableMode.UPDATE){
      this._projectInstallments = this._projectInstallments.map(
        (item: ProjectEquityInstallment) => item.id == installment.id ? installment : item
      )
      this.projectInstallments.next(this._projectInstallments)
    }
    if(mode===UpdateInstallmentObservableMode.REMOVE){
      this._projectInstallments = this._projectInstallments.filter(
        (item: ProjectEquityInstallment) => item.id !== installment.id
      )
      installment.projectInstallmentFundings.forEach((funding:ProjectEquityInstallmentFunding) => this.updateInstallmentFundings(UpdateInstallmentObservableMode.REMOVE, funding))
      this.projectInstallments.next(this._projectInstallments)
    }
  }

  public refetchInstallment(id:string){
    return this.apollo.query({
      query: get_installment_by_id,
      fetchPolicy: 'network-only',
      variables: {
        id: id
      },
    })
  }

  public refetchFunding(id:string){
    return this.apollo.query({
      query: get_funding_by_id,
      fetchPolicy: 'network-only',
      variables: {
        id: id
      }
    })
  }


  public updateInstallment(details:any){
    return this.apollo.mutate({
      mutation: update_equity_installment,
      variables: {
        id: details.id,
        input: {
          installment_number: details.installment_number,
          installment_amount: details.installment_amount,
          project_installment_status_id: details.project_installment_status_id,
          original_commitment_date: details.original_commitment_date,
          notes: details.notes
        }
      }
    })
  }

  public createInstallment(details:any){
    return this.apollo.mutate({
      mutation: create_equity_installment,
      variables: {
        project_id: details.project_id,
        input: {
          installment_number: details.installment_number,
          installment_amount: details.installment_amount,
          original_commitment_date: details.original_commitment_date,
          notes: details.notes,
          certificated: details.certificated,
          active: true
        }
      }
    })
  }

  public deleteInstallment(id: string){
    return this.apollo.mutate({
      mutation: delete_equity_installment,
      variables: {
        id: id
      }
    });

  }

  public deletePackage(id: string){
    return this.apollo.mutate({
      mutation: delete_package,
      variables: {id}
    })
  }

  public createInstallmentFunding(details:any){
    return this.apollo.mutate({
      mutation: create_installment_funding,
      variables: {
        input: {
          project_installment_id: details.project_installment_id,
          funding_number: details.funding_number,
          funding_amount: null,
          funding_status_date: details.date,
          project_installment_funding_status_id: details.project_installment_funding_status_id,
          total_adjuster_amount: null,
          active: true
        }
      }
    })
  }
  public updateInstallmentFunding(details:any){
    return this.apollo.mutate({
      mutation: update_installment_funding,
      variables: {
        id: details.id,
        input: {
          project_installment_funding_status_id: details.project_installment_funding_status_id,
          funding_amount: details.funding_amount,
          funding_status_date: details.funding_status_date,
          total_adjuster_amount: details.total_adjuster_amount
        }
      }
    })
  }

  public deleteInstallmentFunding(id: string){
    return this.apollo.mutate({
      mutation: delete_installment_funding,
      variables: {
        id: id
      }
    })
  }

  public updateInstallmentFundings(mode: UpdateInstallmentObservableMode, funding: ProjectEquityInstallmentFunding){
    if(mode===UpdateInstallmentObservableMode.REMOVE){
      let updatedList = this._installmentFundings[funding.projectInstallment.id].filter(
        (fg:ProjectEquityInstallmentFunding) => fg.id !== funding.id
      )
      this._installmentFundings[funding.projectInstallment.id] = updatedList;
      funding.projectInstallmentFundingConditions.forEach((condition: ProjectInstallmentFundingConditions) => this.updateFundingConditions(UpdateInstallmentObservableMode.REMOVE, condition))
      this.installmentFundings.next(this._installmentFundings)

    }
    if(mode===UpdateInstallmentObservableMode.ADD){
      if(this._installmentFundings[funding.projectInstallment.id].length == 0){
        this._installmentFundings[funding.projectInstallment.id] = []
      }
      this._installmentFundings[funding.projectInstallment.id].push(funding);
      this.installmentFundings.next(this._installmentFundings)
      this._projectFundingPayments[funding.id] = funding.projectInstallmentFundingPayments
      this.projectFundingPayments.next(this._projectFundingPayments)
      this._projectFundingConditions[funding.id] = []
      this.projectFundingConditions.next(this._projectFundingConditions)
      if(funding.projectInstallmentFundingConditions.length>0){
        funding.projectInstallmentFundingConditions.forEach((condition: ProjectInstallmentFundingConditions) => {
          this.updateFundingConditions(UpdateInstallmentObservableMode.ADD, condition)
        })
      }
    }
    if(mode===UpdateInstallmentObservableMode.UPDATE){
      let updatedArray = this._installmentFundings[funding.projectInstallment.id].map(
        (item: ProjectEquityInstallmentFunding) => item.id == funding.id ? funding : item
      );
      this._installmentFundings[funding.projectInstallment.id] = updatedArray
      this.installmentFundings.next(this._installmentFundings)
    }
  }

  private _sortList(type:string, list:any){
    if(type=='conditions'){
      for(let val of Object.keys(list)){
        list[val] = list[val].sort((a:ProjectInstallmentFundingConditions, b: ProjectInstallmentFundingConditions) => a.condition_number - b.condition_number)
      }
    }
  }

  public updateFundingConditions(mode: UpdateInstallmentObservableMode, condition: ProjectInstallmentFundingConditions){
    if(mode===UpdateInstallmentObservableMode.ADD){
      if(this._projectFundingConditions[condition.projectInstallmentFunding.id].length == 0){
        this._projectFundingConditions[condition.projectInstallmentFunding.id] = []
      }
      this._projectFundingConditions[condition.projectInstallmentFunding.id].push(condition)
      this._sortList('conditions', this._projectFundingConditions)
      this.projectFundingConditions.next(this._projectFundingConditions)
      this._projectConditionApprovals[condition.id] = condition.projectInstallmentFundingConditionApprovals
      this.projectConditionApprovals.next(this._projectConditionApprovals)
      this._projectConditionFiles[condition.id] = condition.projectInstallmentFundingConditionFiles
      this.projectConditionFiles.next(this._projectConditionFiles)
    }
    if(mode===UpdateInstallmentObservableMode.REMOVE){
      let updatedList = this._projectFundingConditions[condition.projectInstallmentFunding.id].filter(
        (item: ProjectInstallmentFundingConditions) => item.id !== condition.id
      )
      this._projectFundingConditions[condition.projectInstallmentFunding.id] = updatedList;
      this._sortList('conditions', this._projectFundingConditions)
      this.projectFundingConditions.next(this._projectFundingConditions)

    }
    if(mode===UpdateInstallmentObservableMode.UPDATE){
      let updatedList = this._projectFundingConditions[condition.projectInstallmentFunding.id].map(
        (item: ProjectInstallmentFundingConditions) => item.id == condition.id ? condition : item
      )
      this._projectFundingConditions[condition.projectInstallmentFunding.id] = updatedList.sort((a: ProjectInstallmentFundingConditions, b:ProjectInstallmentFundingConditions) => a.condition_number - b.condition_number)
      this._sortList('conditions', this._projectFundingConditions)
      this.projectFundingConditions.next(this._projectFundingConditions)
      this._projectConditionApprovals[condition.id] = condition.projectInstallmentFundingConditionApprovals
      this.projectConditionApprovals.next(this._projectConditionApprovals)
      this._projectConditionFiles[condition.id] = condition.projectInstallmentFundingConditionFiles
      this.projectConditionFiles.next(this._projectConditionFiles)

    }
  }

  public createFundingCondition(details:any){
    return this.apollo.mutate({
      mutation: create_project_installment_funding_condition,
      variables: {
        input: {
          project_installment_funding_id: details.project_installment_funding_id,
          condition_number: details.condition_number,
          name: details.name,
          nickname: details.nickname,
          include_in_package: true,
          project_installment_funding_condition_status_id: details.project_installment_funding_condition_status_id,
          condition_status_date: details.condition_status_date,
          notes: details.notes,
          additional_notes: details.additional_notes,
          active: true
        }
      }
    })
  }

  public updateFundingCondition(details:any){
    return this.apollo.mutate({
      mutation: update_project_installment_funding_condition,
      variables: {
        id: details.id,
        input: {
          condition_number: details.condition_number,
          name: details.name,
          nickname: details.nickname,
          include_in_package: details.include_in_package,
          project_installment_funding_condition_status_id: details.project_installment_funding_condition_status_id,
          notes: details.notes,
          additional_notes: details.additional_notes,
          condition_status_date: details.condition_status_date
        }
      }
    })
  }

  public deleteFundingCondition(id:string){
    return this.apollo.mutate({
      mutation: delete_project_installment_funding_condition,
      variables: {
        id: id
      }
    })
  }

  public createFundingConditionFile(details:any){
    return this.apollo.mutate({
      mutation: create_funding_condition_file,
      variables: {
        input: {
          project_installment_funding_condition_id: details.project_installment_funding_condition_id,
          filename: details.filename,
          fileurl: details.fileurl,
          include_in_package: true,
          active: true
        }
      }
    })
  }

  public updateIncludeInPackageStatus(id:string, include_in_package: boolean){
    return this.apollo.mutate({
      mutation: update_include_in_package_status,
      variables: {
        id: id,
        include_in_package: include_in_package
      }
    })
  }

  public updateConditionApprovals(mode:UpdateInstallmentObservableMode, approval: ProjectInstallmentFundingConditionApproval){
    if(mode===UpdateInstallmentObservableMode.ADD){
      if(this._projectConditionApprovals[approval.projectInstallmentFundingCondition.id].length == 0){
        this._projectConditionApprovals[approval.projectInstallmentFundingCondition.id] = []
      }
      this._projectConditionApprovals[approval.projectInstallmentFundingCondition.id].push(approval)
      this.projectConditionApprovals.next(this._projectConditionApprovals)
    }
    if(mode===UpdateInstallmentObservableMode.UPDATE){
      let updatedList = this._projectConditionApprovals[approval.projectInstallmentFundingCondition.id].map(
        (item: ProjectInstallmentFundingConditionApproval) => item.id == approval.id ? approval : item
      )
      this._projectConditionApprovals[approval.projectInstallmentFundingCondition.id] = updatedList
      this.projectConditionApprovals.next(this._projectConditionApprovals)
    }
    if(mode===UpdateInstallmentObservableMode.REMOVE){
      let updatedList = this._projectConditionApprovals[approval.projectInstallmentFundingCondition.id].filter(
        (item: ProjectInstallmentFundingConditionApproval) => item.id !== approval.id
      )
      this._projectConditionApprovals[approval.projectInstallmentFundingCondition.id] = updatedList;
      this.projectConditionApprovals.next(this._projectConditionApprovals)

    }
  }
  public createConditionApproval(details:any){
    return this.apollo.mutate({
      mutation: create_condition_approval,
      variables: {
        input: {
          project_installment_funding_condition_id: details.project_installment_funding_condition_id,
          project_installment_funding_condition_approval_status_id: null,
          project_installment_funding_condition_approval_type_id: details.project_installment_funding_condition_approval_type_id,
          approval_status_date: null,
          user_id: null,
          active: true
        }
      }
    })
  }

  public fetchGeneratedPayments(id:string, msgCallback?:Function){
    this.apollo.query({
      query: get_installment_funding_payments,
      fetchPolicy: 'network-only',
      variables: {
        project_installment_funding_id: id
      }
    }).subscribe((response:any) => {
      let rs = response
      let data = rs.data.project_installment_funding_payments;
      this._projectFundingPayments[id] = data;
      if(this._projectFundingPayments[id].length == 0 && msgCallback){
        msgCallback()
      }
      this.projectFundingPayments.next(this._projectFundingPayments)
    })
  }

  public updateFundingPayment(details:any){
    return this.apollo.mutate({
      mutation: update_installment_funding_payment,
      variables: {
        id: details.id,
        amount: details.amount,
        adjuster: details.adjuster
      }
    })
  }

  public deleteFundingPayment(id:string){
    return this.apollo.mutate({
      mutation: delete_installment_funding_payment,
      variables: {
        id: id
      }
    })
  }

  public generatePayments(id: string){
    return this.apollo.mutate({
      mutation: generate_installment_funding_payments,
      variables: {
        project_installment_funding_id: id
      }
    });
  }

  public updatePayments(mode: UpdateInstallmentObservableMode, payment: any, funding_id: string){
    if(mode===UpdateInstallmentObservableMode.ADD){

    }
    if(mode===UpdateInstallmentObservableMode.UPDATE){

    }
    if(mode===UpdateInstallmentObservableMode.REMOVE){
      let updatedList = this._projectFundingPayments[funding_id].map(
        (item: ProjectInstallmentFundingConditions) => item.id == payment.id ? payment : item
      )
      this._projectFundingPayments[funding_id] = updatedList
      this.projectFundingPayments.next(this._projectFundingPayments)
    }
  }

  public fetchApprovalHistoryLogs(id:string){
    return this.apollo.query({
      query: get_log_history,
      variables: {
        project_installment_funding_condition_id: id
      },
      fetchPolicy: 'network-only'
    })
  }

  public updateApprovalStatus(id: string, status_id: string, user_id: string){
    return this.apollo.mutate({
      mutation: update_condition_approval,
      variables: {
        id: id,
        user_id: user_id,
        project_installment_funding_condition_approval_status_id: status_id
      }
    })
  }

  public deleteApproval(id:string){
    return this.apollo.mutate({
      mutation: delete_condition_approval,
      variables: {
        id: id
      }
    })
  }

  public updateConditionFiles(mode: UpdateInstallmentObservableMode, file: ProjectInstallmentFundingConditionFile){
    if(mode===UpdateInstallmentObservableMode.ADD){
      if(this._projectConditionFiles[file.projectInstallmentFundingCondition.id].length == 0){
        this._projectConditionFiles[file.projectInstallmentFundingCondition.id] = []
      }
      this._projectConditionFiles[file.projectInstallmentFundingCondition.id].push(file)
      this.projectConditionFiles.next(this._projectConditionFiles)
    }
    if(mode===UpdateInstallmentObservableMode.UPDATE){
      let updatedList = this._projectConditionFiles[file.projectInstallmentFundingCondition.id].map(
        (item: ProjectInstallmentFundingConditionApproval) => item.id == file.id ? file : item
      )
      this._projectConditionFiles[file.projectInstallmentFundingCondition.id] = updatedList
      this.projectConditionFiles.next(this._projectConditionFiles)
    }
    if(mode===UpdateInstallmentObservableMode.REMOVE){
      let updatedList = this._projectConditionFiles[file.projectInstallmentFundingCondition.id].filter(
        (item: ProjectInstallmentFundingConditions) => item.id !== file.id
      )
      this._projectConditionFiles[file.projectInstallmentFundingCondition.id] = updatedList
      this.projectConditionFiles.next(this._projectConditionFiles)

    }
  }

  public deleteConditionFile(id:string){
    return this.apollo.mutate({
      mutation: delete_condition_file,
      variables: {
        id: id
      }
    })
  }

  public checkPaymentTotals(payments: ProjectInstallmentFundingPayment[], funding: ProjectEquityInstallmentFunding, isAdjustCalc?: boolean){
    let paymentAmount = 0;
    let fundingAmount = funding.funding_amount ?? 0
    let paymentAdj = 0;
    let fundingAdj = funding.total_adjuster_amount ?? 0
    if(payments.length===0){
      return true;
    }
    payments.forEach((payment: ProjectInstallmentFundingPayment) => {
      paymentAmount += payment.amount
      paymentAdj += payment.adjuster
    })
    if(isAdjustCalc){
      return paymentAdj === fundingAdj
    } else {
      return paymentAmount === fundingAmount
    }
  }

  public openSnackBar(message: string, panel_class:string){
    const config = new MatSnackBarConfig();
    config.panelClass = panel_class !== null ? [panel_class] : ['save-success'];
    config.horizontalPosition = "center";
    config.verticalPosition = "top";
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }

  public checkPermissions(permission: Permission, permKey: keyof Permission, key?:string,){
    let canEdit = checkPermission(permission, permKey, key)
    return canEdit;
  }

  public generatePackage(funding_id: ID)
  {
    let sub: Subscription;
    sub = this.apollo.mutate({
      mutation: generate_package,
      variables:{
        project_installment_funding_id: funding_id,
      }
    }).subscribe((response:any) => {
      this.openSnackBar("Package Generation Queued", "success");
      sub?.unsubscribe();
    });
  }
}
